/**
 * 路由守卫（EB 登录 + menus 侧栏 + 静态路由模块）
 */
import { ElMessage, ElMessageBox } from 'element-plus';
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import { getToken } from '@/utils/auth';
import { isRelogin } from '@/api/request';
import useUserStore from '@/store/modules/user';
import useSettingsStore from '@/store/modules/settings';
import getPageTitle from '@/utils/get-page-title';
import usePermissionStore from '@/store/modules/permission';
import useCrmebMenuStore from '@/store/modules/menu';
import useCrmebThemeConfigStore from '@/store/modules/themeConfig';
import { findFirstMenuUrl } from '@/utils/crmebMenu';
import { MERCHANT_DASHBOARD_PATH, MERCHANT_LOGIN_PATHS, isMerchantLoginPath } from '@/config/merchant-route';
import { roterPre } from '@/settings';
import router from '@/router';
import { setBootLoading } from '@/utils/bootState';

NProgress.configure({ showSpinner: false });

// 对齐 EB：`${roterPre}/login` 必须在白名单，否则 /merchant/login 会被改写成 /login?redirect=/merchant/login
const whiteList = [...MERCHANT_LOGIN_PATHS, '/register', '/agreement', '/siteClosed', `${roterPre}/siteClosed`];

function isCrmebSessionReady() {
    const userStore = useUserStore();
    const crmebMenu = useCrmebMenuStore();
    return userStore.roles.includes('crmeb') && crmebMenu.loaded;
}

async function bootstrapCrmebSession() {
    const userStore = useUserStore();
    const crmebMenu = useCrmebMenuStore();
    const permissionStore = usePermissionStore();
    useCrmebThemeConfigStore().applyCssVars();

    if (!crmebMenu.loaded) {
        crmebMenu.restoreFromStorage();
    }
    await userStore.initCrmebSession();
    await permissionStore.generateCrmebRoutes();
}

/** 编辑页离开确认（对齐 EB permission.js + settings.isEdit） */
function confirmLeaveEditPage(
    from: RouteLocationNormalized,
    to: RouteLocationNormalized,
    next: NavigationGuardNext,
    proceed: () => void,
) {
    const settings = useSettingsStore();
    if (!settings.isEdit) {
        proceed();
        settings.setEdit(false);
        return;
    }
    if (from.path === to.path && from.name === to.name) {
        proceed();
        return;
    }
    ElMessageBox.confirm('离开该编辑页面，已编辑信息会丢失，请问您确认离开吗？', '提示', {
        confirmButtonText: '离开',
        cancelButtonText: '不离开',
        type: 'warning',
    })
        .then(() => {
            settings.setEdit(false);
            proceed();
        })
        .catch(() => next(false));
}

function resolveCrmebHomePath(): string {
    const crmebMenu = useCrmebMenuStore();
    return findFirstMenuUrl(crmebMenu.menuTree) || MERCHANT_DASHBOARD_PATH;
}

router.beforeEach((to, from, next) => {
    NProgress.start();
    if (getToken()) {
        document.title = getPageTitle(to.meta.title as string);
        if (isMerchantLoginPath(to.path)) {
            next({ path: resolveCrmebHomePath() });
            NProgress.done();
            return;
        }
        confirmLeaveEditPage(from, to, next, () => {
            if (!isCrmebSessionReady()) {
                isRelogin.show = true;
                // 会话/菜单解析中：显示加载层，导航挂起，受保护内容与菜单不渲染
                setBootLoading(true);
                bootstrapCrmebSession()
                    .then(() => {
                        isRelogin.show = false;
                        next({ ...to, replace: true });
                    })
                    .catch((err) => {
                        // console.log('TiTa 会话初始化失败', err);
                        isRelogin.show = false;
                        useUserStore()
                            .logout()
                            .then(() => {
                                ElMessage.error(typeof err === 'string' ? err : err?.message || '请重新登录');
                                next(`${roterPre}/login?redirect=${to.fullPath}`);
                            });
                    });
                return;
            }
            if (to.path === '/' || to.path === '') {
                const home = resolveCrmebHomePath();
                if (home && home !== to.path) {
                    next({ path: home, replace: true });
                    return;
                }
            }
            next();
        });
        return;
    } else {
        if (whiteList.indexOf(to.path) !== -1) {
            // console.log('在免登录白名单，直接进入');
            next();
        } else {
            // console.log('否则全部重定向到登录页,', to.fullPath);
            next(`${roterPre}/login?redirect=${to.fullPath}`);
            NProgress.done();
        }
    }
});

router.onError(() => {
    NProgress.done();
    setBootLoading(false);
});

router.afterEach(() => {
    NProgress.done();
    // 导航完成（含重定向到登录 / 动态路由加载后的重进）后撤掉加载层
    setBootLoading(false);
});

export { findFirstMenuUrl };
